import React, { useRef, useEffect, useState } from "react";
import s from "./style.module.css";
import "./style.css";
import Vizualisation from "../../../../components/Vizualisation";
import ToggleButtons from "../../../../components/ToggleButtons";
import useVizualisation from "../../../../helpers/useVizualisation";
import { select } from "d3-selection";
import { scaleLinear, scaleOrdinal } from "d3-scale";
import { axisBottom, axisRight } from "d3-axis";
import {
  area,
  curveCatmullRom,
  stack,
  stackOffsetNone,
  stackOffsetExpand,
} from "d3-shape";
import { format } from "d3-format";
import servicesExportData from "../../data/servicesExport.json";
import { Text } from "@visx/text";
import { useSprings, animated } from "react-spring";
import { easeSinInOut } from "d3-ease";
import ColorLegend from "../../../../components/ColorLegend/colorLegend";

const margin = {
  top: 20,
  right: 40,
  bottom: 50,
  left: 10,
};

const AreaChart = ({
  data,
  title,
  note,
  source,
  travel,
  transport,
  computer,
  absolute,
  relative,
  units,
}) => {
  const { ref, width, height, windowSize, inView } = useVizualisation();

  const series = [
    "Computer, communications and other services",
    "Transport services",
    "Travel services",
    "Other (unclassified)",
  ];

  const colorScale = scaleOrdinal()
    .domain(series)
    .range(["#CD546C", "#3574B6", "#31989E", "#8E9598"]);

  const directLabels = [
    {
      x: 2014,
      y: 3700000000000,
      xrel: 2003,
      yrel: 0.8,
      label: travel,
    },
    {
      x: 2010,
      y: 2050000000000,
      xrel: 2003,
      yrel: 0.5,
      label: transport,
    },
    {
      x: 2010,
      y: 600000000000,
      xrel: 2003,
      yrel: 0.2,
      label: computer,
    },
  ];

  const yAxisLabels = [
    {
      value: 0,
      label: "",
    },
    {
      value: 2000000000000,
      label: "2,000",
    },
    {
      value: 4000000000000,
      label: "4,000",
    },
    {
      value: 6000000000000,
      label: "6,000 " + units,
    },
  ];

  const relativeYAxisLabels = [
    {
      value: 0,
      label: "",
    },
    {
      value: 0.25,
      label: "25%",
    },
    {
      value: 0.5,
      label: "50%",
    },
    {
      value: 0.75,
      label: "75%",
    },
    {
      value: 1,
      label: "100%",
    },
  ];

  const options = [
    { value: "absolute", label: absolute, keyword: "Absolute" },
    { value: "relative", label: relative, keyword: "Relative" },
  ];

  const [selectedValue, setSelectedValue] = useState("absolute");

  const stackData = stack().keys(series);

  const xAxisRef = useRef(null);
  const yAxisRef = useRef(null);

  const chartWidth = width - margin.left - margin.right;
  const chartHeight =
    width < 700
      ? height - margin.top - margin.bottom - 54
      : height - margin.top - margin.bottom;

  const xScale = scaleLinear().domain([1988, 2018.5]).range([0, chartWidth]);

  const yScale = scaleLinear()
    .domain([0, 6000000000000])
    .range([chartHeight, 0]);

  if (selectedValue === "absolute") {
    yScale.domain([0, 6000000000000]);
    stackData.offset(stackOffsetNone);
  }
  if (selectedValue === "relative") {
    yScale.domain([0, 1]);
    stackData.offset(stackOffsetExpand);
  }

  const stackedData = stackData(servicesExportData);

  const areaGenerator = area()
    .x((d) => xScale(d.data.Year))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]))
    .curve(curveCatmullRom.alpha(0.5));

  const xAxis = axisBottom(xScale)
    .tickSize(4)
    .tickValues([1988, 1998, 2008, 2018])
    .tickFormat(format(""));
  const yAxis = axisRight(yScale).tickSize(-chartWidth).ticks(4);
  if (selectedValue === "relative") {
    yAxis.tickValues([0.25, 0.5, 0.75, 1]).tickFormat(format(".0%"));
  }
  const areaStyles = useSprings(
    stackedData.length,
    stackedData.map((d, i) => {
      return {
        config: {
          duration: 1000,
          easing: easeSinInOut,
        },
        d: areaGenerator(d),
        fill: colorScale(d.key),
      };
    })
  );

  const directLabelStyles = useSprings(
    directLabels.length,
    directLabels.map((d, i) => {
      return {
        config: {
          duration: 1000,
          easing: easeSinInOut,
        },
        x: selectedValue === "absolute" ? xScale(d.x) : xScale(d.xrel),
        y: selectedValue === "absolute" ? yScale(d.y) : yScale(d.yrel),
      };
    })
  );

  useEffect(() => {
    select(xAxisRef.current).call(xAxis);
    select(yAxisRef.current).call(yAxis);
  });

  return (
    <>
      <Vizualisation
        title={title}
        note={note}
        source={source}
        ref={ref}
        size="large"
        beforeChart={
          <ToggleButtons
            options={options}
            onChange={setSelectedValue}
            selected={selectedValue}
          />
        }
      >
        {width < 700 && <ColorLegend colorscale={colorScale}></ColorLegend>}
        <svg width={width} height={500}>
          <g
            className="chart"
            transform={`translate(${margin.left}, ${margin.top})`}
          >
            <g className="g-axis">
              <g
                className="axis axis--x"
                transform={`translate(0,${chartHeight})`}
                ref={xAxisRef}
              ></g>
            </g>
            <g className="g-axis">
              <g
                className="axis axis--y"
                transform={`translate(${chartWidth}, 0)`}
                ref={yAxisRef}
              ></g>
              <g>
                {selectedValue === "absolute"
                  ? yAxisLabels.map((d) => {
                      return (
                        <Text
                          x={xScale(2018)}
                          y={yScale(d.value)}
                          textAnchor={"start"}
                          verticalAnchor={"middle"}
                          dx={"4px"}
                          className={s.axisLabel}
                          width={40}
                        >
                          {d.label}
                        </Text>
                      );
                    })
                  : relativeYAxisLabels.map((d) => {
                      return (
                        <Text
                          x={xScale(2018)}
                          y={yScale(d.value)}
                          textAnchor={"start"}
                          verticalAnchor={"middle"}
                          dx={"4px"}
                          className={s.axisLabel}
                          width={80}
                        >
                          {d.label}
                        </Text>
                      );
                    })}
              </g>
            </g>
            {stackedData.map((series, i) => {
              return (
                <animated.path
                  key={series.key}
                  d={areaStyles[i].d}
                  stroke={"#ffffff"}
                  fill={colorScale(series.key)}
                ></animated.path>
              );
            })}
            {width > 699 &&
              directLabels.map((d, i) => {
                return (
                  <animated.text
                    x={directLabelStyles[i].x}
                    y={directLabelStyles[i].y}
                    fill={"#ffffff"}
                    opacity={0.7}
                    textAnchor={"middle"}
                    fontSize={"14px"}
                  >
                    {d.label}
                  </animated.text>
                );
              })}
          </g>
        </svg>
      </Vizualisation>
    </>
  );
};

export default AreaChart;
